/**
 * Minigame: Krabbellijn!
 *
 * De doorfluistertelefoon met krabbels. De spelregels staan in lib/ketting.js;
 * hier staat wat de app ermee doet: ophalen, tekenen, insturen en teruglezen.
 *
 * ---- wat je wel en niet te zien krijgt ----------------------------------
 *
 * Van een keten die nog loopt haalt de app precies één stap op: die ene waar jij
 * op moet reageren. De rest van het verhaal komt niet eens over de lijn. Dat is
 * geen slot op de deur, want wie wil kan de server zelf iets vragen, maar het
 * scheelt dat je niet per ongeluk de clou ziet in een tabblad dat openstond.
 *
 * Voor het overzicht is er een tweede vraag die alleen de skeletjes ophaalt: welke
 * keten, welke stap, van wie. Geen zinnen, geen tekeningen. Daaruit rolt wat er
 * loopt, wat klaar is en waar jij aan de beurt bent.
 *
 * ---- de klok begint pas als jij begint ----------------------------------
 *
 * In het echte spel loopt er een klok en dat hoort erbij: half af is grappiger
 * dan uitgetekend. Maar hier kun je het venster ook per ongeluk openen, en dan
 * zou een klok een lege tekening in de keten van een ander duwen. Daarom begint
 * hij te lopen bij je eerste streek of je eerste letter. Tot dan gebeurt er niks.
 */

import { PB_URL } from '../lib/config.js';
import { api, asJson } from '../lib/api.js';
import { PALETTE_HEX } from '../lib/palette.js';
import { avatarUrl, pixUrl } from '../lib/pixels.js';
import { playKetKlaar, playKetStuur, startLijnMuziek, stopLijnMuziek } from '../lib/sound.js';
import * as ket from '../lib/ketting.js';

const COL = 'paal_ketting';


/** Hoeveel stappen we hoogstens ophalen. Ruim boven wat er ooit zal staan. */
const MAX_RIJEN = 400;

/** Hoe lang geleden, in gewone woorden. */
function geleden(iso) {
  const t = Date.parse(String(iso || '').replace(' ', 'T'));
  if (!t) return '';
  const min = Math.floor((Date.now() - t) / 60000);
  if (min < 1) return 'net';
  if (min < 60) return min + ' min geleden';
  const uur = Math.floor(min / 60);
  if (uur < 24) return uur + (uur === 1 ? ' uur geleden' : ' uur geleden');
  const dag = Math.floor(uur / 24);
  return dag === 1 ? 'gisteren' : dag + ' dagen geleden';
}

export default {
  // ---- openen en sluiten --------------------------------------------------

  /**
   * Openen is meteen spelen.
   *
   * De app kijkt wat er voor jou klaarligt en zet dat neer. Ligt er niets, dan
   * begin je zelf een keten. Er is geen overzichtsscherm met uitleg voor: wat je
   * moet doen zie je, in plaats van dat het je verteld wordt.
   */

  openKetting() {
      // De tegel in de hal staat al uit voor wie niet meedoet, maar het adres
      // #krabbellijn en de knop in de balk komen hier ook langs.
      if (!this.magBeta()) { this.betaAf('De Krabbellijn'); return; }
      // Loopt hij al, dan halen we hem terug in plaats van opnieuw te beginnen. Via
      // de tegel in de hal of #krabbellijn was je anders je halve tekening kwijt.
      if (this.state.ketOpen) {
        return this.vensterTerug('ket', () => { this.ketTeken(); this.ketMuziek(); });
      }
      this.vensterNaarVoren('ket');
      this.ketVel = ket.LEEG;
      this.ketUndoStapel = [];
      this.setState({
        ketOpen: true, vensterMin: Object.assign({}, this.state.vensterMin, { ket: false }),
        notifOpen: false, startOpen: false,
        // Vanaf nu staat hij in de taakbalk, tot je hem met het kruisje dichtdoet.
        // Wegklappen laat hem staan. Zie vensterLijst in logic/venster.js.
        ketMode: 'laden', ketKeten: null, ketVorige: null, ketLezen: null,
        ketZin: '', ketIets: false, ketFout: '', ketBezig: true,
        ketKleur: 0, ketTool: 'pen', ketSel: null, ketVoor: null
        // Waar het programmaatje staat wordt niet hier bewaard maar in de
        // vensterlaag, onder zijn eigen naam. Zie logic/venster.js.
      }, () => { this.ketMuziek(); this.ketAanZet(); });
    },

  /**
   * Het deuntje aan, tenzij het geluid uit staat of je aan het tekenen bent.
   *
   * Tijdens een tekenbeurt zwijgt het met opzet: daar zit je een kwartier naar
   * hetzelfde vel te kijken, en een lus van vijf tellen die dan een kwartier
   * doorspeelt is geen grap meer. Bij het lezen, raden en kiezen speelt hij wel.
   * De luidsprekerknop in de titelbalk zet hem overal uit.
   */
  ketMuziek() {
      const keten = this.state.ketKeten;
      const tekent = this.state.ketMode === 'doe' && keten && ket.soortVan(keten.nr) === 'krabbel';
      const weg = !!(this.state.vensterMin || {}).ket;
      if (this.state.spelStil || tekent || !this.state.ketOpen || weg) {
        stopLijnMuziek();
        return;
      }
      startLijnMuziek();
    },

  /** Wat er ligt ophalen, en dan de beurt neerzetten waar jij aan zet bent. */
  async ketAanZet() {
      await this.ketHaal();
      if (!this.state.ketOpen) return;
      const beurt = this.state.ketMissing ? null : this.ketBeurt();
      if (beurt) await this.ketPak(beurt); else this.ketNieuw();
    },

  /** Loopt er een beurt van iemand anders bij jou? */
  ketLoopt() {
      const k = this.state.ketKeten;
      if (!k || k.nr <= 0) return false;
      return this.state.ketMode === 'doe' || this.state.ketMode === 'kiezen';
    },

  /**
   * Het kruisje breekt je beurt af.
   *
   * Wat je had staat niet op de server, dus dat is weg: een zin die je typte, een
   * tekening die je maakte. De beurt zelf blijft wel bij jou liggen tot de klok
   * afloopt, want die klok staat op de regel van de vorige speler en die valt niet
   * terug te draaien. Kom je binnen de tijd terug, dan pak je hem weer op; kom je
   * niet terug, dan ligt hij daarna vrij voor een ander.
   *
   * Teken je op het echte doek, dan komt jouw eigen krabbel hier terug. Zonder dat
   * zou afbreken je met een leeg doek achterlaten waar net nog je eigen werk stond.
   */
  closeKetting() {
      const afgebroken = this.ketLoopt();
      this.ketStopTik();
      stopLijnMuziek();
      this.setState({ ketOpen: false, vensterMin: Object.assign({}, this.state.vensterMin, { ket: false }), ketMode: 'laden', ketKeten: null, ketVorige: null, ketLezen: null, ketZin: '', ketIets: false });
      this.ketVel = ket.LEEG;
      this.ketUndoStapel = [];
      if (afgebroken) this.notify('Beurt afgebroken. Hij blijft bij jou tot de tijd om is; daarna mag een ander.');
    },

  /** Weg bij deze stap: kijken of er een volgende ligt, anders zelf beginnen. */
  ketTerug() {
      this.ketStopTik();
      this.setState({
        ketMode: 'laden', ketKeten: null, ketVorige: null, ketLezen: null,
        ketFout: '', ketBezig: true
      }, this.ketAanZet);
    },

  /** De lijst met klare ketens, en weer terug naar je stap. */
  ketNaarLijst() { return this.setState({ ketMode: 'lijst', ketFout: '' }); },
  ketUitLijst() {
      this.setState({ ketMode: this.state.ketKeten ? 'doe' : 'laden' }, () => {
        setTimeout(this.ketTeken, 0);
        if (!this.state.ketKeten) this.ketAanZet();
      });
    },

  // ---- de server ----------------------------------------------------------

  /**
   * De skeletjes van alle stappen: welke keten, welk nummer, van wie, naar wie,
   * tot wanneer.
   *
   * Met `fields` laat de server de zinnen en de tekeningen weg. Kent een oudere
   * server dat woord niet, dan stuurt hij gewoon alles mee en werkt het ook.
   */
  async ketHaal() {
      if (!PB_URL) return;
      try {
        const res = await api(`${PB_URL}/api/collections/${COL}/records`
          + '?fields=keten,nr,authorId,naarId,tot,created&sort=-created&perPage=' + MAX_RIJEN);
        if (!res.ok) { this.setState({ ketMissing: true, ketStatus: res.status, ketBezig: false }); return; }
        const data = await res.json();
        this.setState({ ketRijen: data.items || [], ketMissing: false, ketStatus: 0, ketBezig: false });
        return data.items || [];
      } catch (e) {
        this.setState({ ketMissing: true, ketStatus: 0, ketBezig: false });
      }
    },

  /**
   * Hoe iemand heet, opgezocht in paal_accounts.
   *
   * De naam staat niet bij de stap op de server. Dat hoeft niet: hij staat al in
   * de accountlijst die de app toch al bijhoudt, en zo volgt een verhaal iemands
   * naam als die zich morgen anders noemt. Zie ook requests.js, waar het net zo
   * gaat.
   */
  ketNaam(id) {
      if (!id) return 'iemand';
      const a = (this.state.accounts || []).find((x) => x.id === id);
      return (a && a.name) || 'iemand';
    },

  ketKetens() { return ket.ketens(this.state.ketRijen || []); },
  ketBeurt() { return ket.jouwBeurt(this.ketKetens(), this.state.accountId || '', Date.now()); },
  ketVrije() { return ket.vrijeBeurten(this.ketKetens(), this.state.accountId || '', Date.now()); },

  /**
   * Een beurt aannemen: de stap ophalen waar je op moet reageren, en verder niks.
   *
   * Moet je tekenen en zit je achter een computer, dan gaat het doek open en komt
   * dit programmaatje ernaast te staan. Op een telefoon bestaat het tekenscherm
   * niet (zie computeCanvasSize in logic/lifecycle.js), en dan teken je in het
   * programmaatje zelf op een klein doek. Beide leveren hetzelfde vel van
   * duizendvierentwintig tekens op, dus de keten merkt het verschil niet.
   */
  async ketPak(k) {
      if (!k || !ket.ketenIdOk(k.id)) return this.ketNieuw();
      this.setState({ ketBezig: true, ketFout: '' });
      try {
        const res = await api(`${PB_URL}/api/collections/${COL}/records`
          + `?filter=(keten='${k.id}'%26%26nr=${k.volgende - 1})&sort=created&perPage=1`);
        if (!res.ok) { this.setState({ ketFout: 'De vorige stap is niet op te halen.', ketBezig: false }); return; }
        const vorige = ((await res.json()).items || [])[0];
        if (!vorige) { this.setState({ ketFout: 'De vorige stap is niet op te halen.', ketBezig: false }); return; }
        this.ketBegin({ id: k.id, nr: k.volgende, tot: k.tot }, vorige);
      } catch (e) {
        this.setState({ ketFout: 'De vorige stap is niet op te halen.', ketBezig: false });
      }
    },

  /** Zelf een keten beginnen: stap nul, en dat is altijd een zin. */
  ketNieuw() {
      this.ketBegin({ id: ket.nieuwKetenId(), nr: 0, tot: 0 }, null);
    },

  /**
   * Het beurtscherm klaarzetten.
   *
   * Tekenen gebeurt altijd in het programmaatje zelf, op een computer net zo goed
   * als op een telefoon. Het nam eerst het echte tekendoek over, en dat werkte,
   * maar het kostte een hele stoet: jouw eigen onafgemaakte krabbel opzijzetten en
   * terugzetten, het automatische herstel van dat werk uitschakelen, en de knop
   * Plakken! blokkeren zodat een geheime ketenstap niet aan de paal kwam te
   * hangen. Drie plekken buiten dit bestand die alle drie op precies het juiste
   * moment moesten meewerken. Nu is het venster het doek en is dat allemaal weg.
   */
  ketBegin(keten, vorige) {
      this.ketVel = ket.LEEG;
      this.ketUndoStapel = [];
      this.setState({
        ketMode: 'doe', ketKeten: keten, ketVorige: vorige || null,
        ketZin: '', ketIets: false, ketFout: '', ketBezig: false,
        ketKleur: 0, ketTool: 'pen', ketSel: null, ketVoor: null
      }, () => {
        setTimeout(this.ketTeken, 0);
        this.ketTik();
        this.ketMuziek();
      });
    },

  /**
   * Klaar met je stap: nu kiezen naar wie hij gaat.
   *
   * Eerst het vel vastleggen, want daarna gaat het scherm naar de kiezer en kan er
   * op het doek nog van alles gebeuren.
   */
  ketKlaar() {
      const keten = this.state.ketKeten;
      if (!keten) return;
      const soort = ket.soortVan(keten.nr);
      if (soort === 'zin') {
        if (!ket.zinOk(this.state.ketZin)) { this.setState({ ketFout: 'Schrijf eerst een regel.' }); return; }
      } else if (!ket.krabbelOk(this.ketVel)) {
        this.setState({ ketFout: 'Teken eerst iets.' }); return;
      }
      if (!this.state.accountId) { this.setState({ ketFout: 'Log eerst in, anders weet niemand van wie dit is.' }); return; }
      this.setState({ ketMode: 'kiezen', ketFout: '' });
    },

  /**
   * Insturen, naar één iemand, met een kwartier de tijd.
   *
   * Eén nieuwe regel op de server, nooit een bestaande aanpassen. Twee mensen
   * kunnen dezelfde stap tegelijk insturen; dan staan er twee regels met hetzelfde
   * nummer en telt de oudste. Zie ketens() in lib/ketting.js.
   */
  async ketStuur(ontvanger) {
      const keten = this.state.ketKeten;
      if (!keten || this.state.ketBezig || !ontvanger || !ontvanger.id) return;
      const soort = ket.soortVan(keten.nr);
      this.setState({ ketBezig: true, ketFout: '' });
      try {
        /**
         * Zeven velden, en geen acht of tien.
         *
         * `soort` stond er ook: die volgde uit `nr` (even is een zin, oneven een
         * tekening) en kon dus alleen maar met dat nummer in tegenspraak komen te
         * staan. En de namen stonden erbij, van de schrijver en van de ontvanger;
         * die staan al in paal_accounts. Zie ketNaam().
         */
        const res = await api(`${PB_URL}/api/collections/${COL}/records`, asJson('POST', {
          keten: keten.id, nr: keten.nr,
          tekst: soort === 'zin' ? String(this.state.ketZin || '').trim().slice(0, ket.ZIN_MAX) : '',
          pixels: soort === 'krabbel' ? (this.ketVel || ket.LEEG) : '',
          authorId: this.state.accountId,
          naarId: ontvanger.id,
          // Het tempo van de keten volgt uit de regel zelf; zie isSnel() in
          // lib/ketting.js. Begin je een keten, dan koos je het net; ga je verder
          // op een bestaande, dan houd je het tempo aan waar hij al op loopt.
          tot: Date.now() + ket.beurtDuur(
            keten.nr === 0 ? this.state.ketSnel : ket.isSnel(this.ketKetens().find((k) => k.id === keten.id) || null),
            keten.nr + 1
          )
        }));
        if (!res.ok) {
          this.setState({ ketBezig: false, ketMode: 'doe', ketFout: res.status === 403
            ? 'De server neemt hem niet aan; de regels van de collectie staan dicht.'
            : 'Insturen lukte niet (' + res.status + ').' });
          return;
        }
        const vol = keten.nr >= ket.STAPPEN - 1;
        this.spelGeluid(vol ? playKetKlaar : playKetStuur);
        const mijn = await res.json().catch(() => null);
        const voor = await this.ketWasIemandVoor(keten, mijn);
        this.notify(voor
          ? 'Iemand was net iets sneller met deze stap, dus die van jou doet niet mee.'
          : vol ? 'Keten vol! Hier is het hele verhaal.' : 'Doorgegeven aan ' + (ontvanger.name || 'iemand') + '.');
        this.setState({ ketBezig: false });
        if (vol && !voor) this.ketLees(keten.id); else this.ketTerug();
      } catch (e) {
        this.setState({ ketBezig: false, ketMode: 'doe', ketFout: 'Insturen lukte niet.' });
      }
    },

  // ---- de klok ------------------------------------------------------------

  /**
   * De klok telt af naar een tijdstip op de server, niet vanaf nu.
   *
   * Dit tikje bestaat alleen om het scherm elke seconde opnieuw te laten tekenen.
   * De echte tijd staat in `tot` op de regel van de vorige speler, dus sluit je de
   * app en kom je terug, dan klopt de klok nog steeds.
   */
  ketTik() {
      this.ketStopTik();
      this.ketTimer = setInterval(() => {
        if (!this.state.ketOpen) { this.ketStopTik(); return; }
        const k = this.state.ketKeten;
        if (k && k.tot && Date.now() >= k.tot) {
          // Je tijd is om. De keten ligt weer vrij voor een ander.
          this.ketStopTik();
          this.notify('Je kwartier is om; deze keten ligt weer vrij.');
          this.ketTerug();
          return;
        }
        // Hertekenen alleen als je hem ziet. De controle hierboven of je tijd om is
        // moet wél doorlopen als hij weggeklapt is, want die geeft de keten vrij.
        if (!(this.state.vensterMin || {}).ket) this.setState({ ketNu: Date.now() });
      }, 1000);
    },

  ketStopTik() {
      if (this.ketTimer) { clearInterval(this.ketTimer); this.ketTimer = null; }
    },

  // ---- typen --------------------------------------------------------------

  ketTyp(e) {
      const t = String(e.target.value || '').slice(0, ket.ZIN_MAX);
      this.setState({ ketZin: t, ketFout: '' });
    },

  ketZinToets(e) {
      if (e.key === 'Enter') { e.preventDefault(); this.ketStuur(); }
    },

  // ---- tekenen ------------------------------------------------------------

  /** Van een muis- of vingerplek naar een vakje op het raster. */
  ketPos(e) {
      const cv = this.ketRef && this.ketRef.current;
      if (!cv) return null;
      const r = cv.getBoundingClientRect();
      // Binnen de rand rekenen: de doos van het canvas telt zijn rand van 3 mee, en
      // dan liep de muis aan de randen een kwart vakje voor op de streep.
      const w = cv.clientWidth || r.width, h = cv.clientHeight || r.height;
      if (!w || !h) return null;
      const x = Math.floor((e.clientX - r.left - (cv.clientLeft || 0)) * (ket.VAK / w));
      const y = Math.floor((e.clientY - r.top - (cv.clientTop || 0)) * (ket.VAK / h));
      if (x < 0 || y < 0 || x >= ket.VAK || y >= ket.VAK) return null;
      return { x, y };
    },

  /**
   * Het teken dat je nu in je kwast hebt: een cijfer uit het palet, of niks.
   *
   * Net als in de avatar-tekenaar is de gum geen aparte knop maar een kleur die
   * er niet is: `ketKleur` op min één. Zo hoef je nergens twee dingen tegelijk
   * bij te houden, en kan een gum ook een vlak leegmaken.
   */
  ketTeken1() { return this.state.ketKleur < 0 ? '.' : (this.state.ketKleur || 0).toString(16); },

  /**
   * ---- de vinger of de muis op het doek ---------------------------------
   *
   * Twee gereedschappen, net als bij de avatar: het potlood en het kader. Met het
   * kader wijs je een vlak aan; tik je daarna binnen dat vlak, dan versleep je de
   * pixels, en kies je een kleur, dan vult hij het vlak in één keer.
   *
   * Dit hangt aan pointer-events en niet aan mouse-events, en dat is het verschil
   * met de avatar-tekenaar: daar werkt slepen op een telefoon niet, want een
   * vinger stuurt geen mouseenter. Hier stuurt hij precies dezelfde berichten als
   * een muis, dus tekenen, kaderen en verslepen doen het met een vinger net zo.
   */
  ketDown(e) {
      if (this.state.ketBezig) return;
      const p = this.ketPos(e);
      if (!p) return;
      try { e.target.setPointerCapture(e.pointerId); } catch (er) {}
      // Voor het terugdraaien: het vel zoals het was vóór deze streek of sleep.
      // Eén handeling is één stap terug, en niet elk los vakje.
      this.ketUndoStapel = (this.ketUndoStapel || []).concat([this.ketVel]).slice(-24);
      if (this.state.ketTool === 'sel') {
        if (ket.inVak(this.state.ketSel, p.x, p.y)) {
          // Verslepen. Het vel van vóór de sleep blijft staan als basis, zodat elke
          // beweging vanaf hetzelfde punt rekent en de pixels niet uitsmeren.
          this.ketSleep = { vanaf: p, vak: this.state.ketSel, basis: this.ketVel };
        } else {
          this.ketKaderen = { a: p };
          this.setState({ ketSel: null, ketVoor: { a: p, b: p } });
        }
        return;
      }
      this.ketVerft = true;
      this.ketLaatst = p;
      this.ketVerf(p.x, p.y);
    },

  ketMove(e) {
      const p = this.ketPos(e);
      if (!p) return;
      if (this.ketSleep) {
        const r = this.ketSleep.vak;
        const klem = (n, max) => Math.max(0, Math.min(max, n));
        const nx = klem(r.x + (p.x - this.ketSleep.vanaf.x), ket.VAK - r.w);
        const ny = klem(r.y + (p.y - this.ketSleep.vanaf.y), ket.VAK - r.h);
        this.ketVel = ket.verplaats(this.ketSleep.basis, r, nx, ny);
        this.setState({ ketSel: { x: nx, y: ny, w: r.w, h: r.h } }, this.ketTeken);
        return;
      }
      if (this.ketKaderen) {
        this.setState({ ketVoor: { a: this.ketKaderen.a, b: p } });
        return;
      }
      if (!this.ketVerft) return;
      const van = this.ketLaatst || p;
      ket.lijn(van.x, van.y, p.x, p.y).forEach(([x, y]) => this.ketVerf(x, y));
      this.ketLaatst = p;
    },

  ketUp() {
      if (this.ketSleep) {
        this.ketSleep = null;
        this.setState({ ketIets: /[0-9a-f]/.test(this.ketVel || '') });
        return;
      }
      if (this.ketKaderen) {
        const voor = this.state.ketVoor;
        this.ketKaderen = null;
        // Eén tik zonder slepen laat het kader weer los; dat is de uitgang.
        const r = voor ? ket.rechthoek(voor.a, voor.b) : null;
        const punt = r && r.w === 1 && r.h === 1;
        this.setState({ ketSel: punt ? null : r, ketVoor: null });
        return;
      }
      this.ketVerft = false;
      this.ketLaatst = null;
    },

  /**
   * Eén vakje verven: in het vel én meteen op het doek.
   *
   * Het vel staat buiten de toestand. Bij elke streek de hele app opnieuw laten
   * tekenen is zonde als er één vakje verandert, en op een raster van
   * tweeëndertig merk je dat.
   */
  ketVerf(x, y) {
      const teken = this.ketTeken1();
      this.ketVel = ket.verf(this.ketVel || ket.LEEG, x, y, teken, 1);
      const cv = this.ketRef && this.ketRef.current;
      const ctx = cv && cv.getContext('2d');
      if (ctx) {
        if (teken === '.') ctx.clearRect(x, y, 1, 1);
        else { ctx.fillStyle = PALETTE_HEX[this.state.ketKleur] || '#000'; ctx.fillRect(x, y, 1, 1); }
      }
      if (!this.state.ketIets) this.setState({ ketIets: true, ketFout: '' });
    },

  /** Het hele vel opnieuw op het doek zetten. Na terugdraaien, wissen en slepen. */
  ketTeken() {
      const cv = this.ketRef && this.ketRef.current;
      const ctx = cv && cv.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, ket.VAK, ket.VAK);
      const vel = this.ketVel || ket.LEEG;
      for (let i = 0; i < vel.length; i++) {
        const ch = vel[i];
        if (ch === '.') continue;
        const kleur = PALETTE_HEX[parseInt(ch, 16)];
        if (!kleur) continue;
        ctx.fillStyle = kleur;
        ctx.fillRect(i % ket.VAK, Math.floor(i / ket.VAK), 1, 1);
      }
    },

  /**
   * Een kleur kiezen. Ligt er een kader, dan vult die kleur het kader in één
   * keer, precies zoals op het grote doek en in de avatar-tekenaar.
   */
  ketKies(n) {
      if (this.state.ketSel) {
        this.ketUndoStapel = (this.ketUndoStapel || []).concat([this.ketVel]).slice(-24);
        this.ketVel = ket.vul(this.ketVel || ket.LEEG, this.state.ketSel, n < 0 ? '.' : n.toString(16));
        this.setState({ ketIets: /[0-9a-f]/.test(this.ketVel), ketFout: '' }, this.ketTeken);
        return;
      }
      this.setState({ ketKleur: n });
    },

  ketPakGereedschap(tool) { return this.setState({ ketTool: tool, ketSel: null, ketVoor: null }); },
  ketPakGum() { return this.ketKies(-1); },

  ketTerugdraai() {
      const stapel = this.ketUndoStapel || [];
      if (!stapel.length) return;
      this.ketVel = stapel[stapel.length - 1];
      this.ketUndoStapel = stapel.slice(0, -1);
      this.setState({ ketIets: /[0-9a-f]/.test(this.ketVel), ketSel: null, ketVoor: null }, this.ketTeken);
    },

  ketWis() {
      this.ketUndoStapel = (this.ketUndoStapel || []).concat([this.ketVel]).slice(-24);
      this.ketVel = ket.LEEG;
      this.setState({ ketIets: false, ketSel: null, ketVoor: null }, this.ketTeken);
    },

  // ---- het programmaatje verslepen ---------------------------------------

  /**
   * Oppakken aan de titelbalk en neerzetten waar je wil.
   *
   * De plek blijft staan zolang de app open is, ook als je het venster tussendoor
   * sluit: zet je hem uit de weg omdat hij over je doek staat, dan hoef je dat
   * niet elke beurt opnieuw te doen.
   *
   * In die balk staan ook het kruisje en de geluidsknop, en die zijn geen greep.
   * Zonder de regel hieronder begon een klik daarop een sleep, en dan legt
   * setPointerCapture alle verdere pointergebeurtenissen van die vinger bij de balk
   * neer in plaats van bij de knop. De browser stuurt de click daarna naar de
   * gedeelde ouder, dus de knop kreeg hem nooit: op een computer was het venster
   * niet te sluiten en het muziekje niet uit te zetten. Op een telefoon werkten ze
   * wel, want daar wordt er niet gesleept.
   */

  ketVals() {
      const mob = this.state.isMobile;
      const ink = 'var(--ink)';
      const lijst = this.ketKetens();
      const keten = this.state.ketKeten;
      const soort = keten ? ket.soortVan(keten.nr) : 'zin';
      const vorige = this.state.ketVorige;
      const vorigeSoort = vorige && keten ? ket.soortVan(keten.nr - 1) : '';
      const lezen = this.state.ketLezen;
      const klaar = lijst.filter((k) => k.klaar);
      const nu = this.state.ketNu || Date.now();
      const over = keten && keten.tot ? ket.resterend(keten, nu) : 0;
      const loopt = this.ketLoopt();
      const tekent = this.state.ketMode === 'doe' && soort === 'krabbel';

      /**
       * ---- waar het programmaatje staat ----
       *
       * Standaard midden op het werkvlak, net als elk venster (vensterStaStyle in
       * logic/venster.js). Sleep je hem weg, dan blijft hij daar.
       * Op een telefoon zweeft er niks: daar is het een strook onderaan, want een
       * los venstertje van 340 pixels op een scherm van 375 is geen venster maar
       * een obstakel.
       */
      /**
       * Het eindscherm is breder dan de rest.
       *
       * Overal is het venster 340: dat is de maat van een programmaatje dat naast
       * je doek staat. Maar in het teruglezen is de strook de hoofdzaak, en op 340
       * zag je anderhalf kaartje. Op 480 zie je er twee met een stukje van de
       * derde, en dan lees je het verhaal in plaats van dat je het bij elkaar
       * scrollt. Alleen daar dus breder, en verder niets veranderd.
       */
      const lees = this.state.ketMode === 'lees';
      // Twee breedtes en geen derde: 340 voor de lichte schermen, 480 zodra het
      // doek of de strook de hoofdzaak is.
      const groot = lees || tekent;
      const breed = groot ? '480px' : '340px';
      const venster = this.vensterVals('ket', breed,
        mob ? '70dvh' : groot ? 'min(620px, 88dvh)' : 'min(460px, 80dvh)',
        { rand: mob ? 14 : 16 });

      const tempoStyle = (aan) => ({
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '1px',
        width: '100%', padding: '8px 10px', boxSizing: 'border-box', textAlign: 'left',
        borderRadius: '12px', minWidth: '0',
        background: aan ? 'var(--paars)' : '#fff', color: aan ? '#fff' : ink
      });

      const knopStyle = (soort2) => ({
        width: '100%', height: '44px', boxSizing: 'border-box', justifyContent: 'center',
        borderRadius: '12px', fontSize: '15px', fontWeight: 600, flex: '0 0 auto',
        background: soort2 === 'roze' ? 'var(--roze)' : '#fff', color: soort2 === 'roze' ? '#fff' : ink
      });

      return {
        // Het venster staat er zodra je een beurt hebt, ook als het spel "dicht"
        // is: je beurt loopt door en het doek moet zichtbaar blijven.
        showKet: this.vensterInfo('ket').open,
        openKetting: this.openKetting,
        closeKetting: this.closeKetting,
        // Het kruisje staat er altijd. Klik je hem weg, dan heb je je beurt
        // afgebroken; wat je had is weg en de beurt ligt bij jou tot de tijd om is.
        // Zie closeKetting.
        showKetKruis: true,
        ketTitel: 'Krabbellijn',
        // Het venster in één sleutel; de gedeelde schil pakt het zelf uit. Zie
        // views/Venster.jsx. Er stonden acht losse sleutels voor precies de dingen
        // die die schil al kent.
        ketVenster: venster,

        /**
         * ---- het venster ----
         *
         * Eén kader, en de inhoud staat er direct in. Er zat eerst een tweede
         * omkaderde plaat in met een eigen rand en schaduw: een wit venster met
         * daarin een lila venster. Dat is diepte in diepte en dat hoort hier niet
         * (zie sectie 5 van CLAUDE.md). Nu is het één programmaatje met een
         * titelbalk, zoals de rest van deze bureaubladwereld.
         */
        /**
         * ---- het venster ----
         *
         * Kader en titelbalk komen uit de vensterlaag; die is voor alle
         * programmaatjes gelijk. Zie logic/venster.js.
         *
         * Niet hoger dan de maat die hieronder meegegeven wordt: een programmaatje
         * dat het halve scherm afdekt is geen programmaatje meer, en wat er niet in
         * past scrolt binnenin. Het eindscherm mag wat hoger, want daar zijn de
         * kaartjes groter.
         */
        // Let op de naam: `this.ketSleep` is binnen dit spel het slepen van een
        // selectie op het tekenvel, niet het slepen van het venster. Die twee botsten
        // op dezelfde naam; het venster heet overal `venster.sleep` en zit nu in
        // `ketVenster` hierboven.
        ketInhoudStyle: {
          padding: mob ? '14px' : '16px', boxSizing: 'border-box',
          display: 'flex', flexDirection: 'column', gap: '10px',
          overflowY: 'auto', overscrollBehavior: 'contain', minHeight: '0'
        },

        // ---- de klok in de titelbalk ----
        showKetKlok: loopt && !!(keten && keten.tot),
        ketKlokLabel: (() => {
          const sec = Math.ceil(over / 1000);
          return Math.floor(sec / 60) + ':' + String(sec % 60).padStart(2, '0');
        })(),
        ketKlokStyle: {
          flex: '0 0 auto', fontSize: '13px', fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          padding: '2px 9px', borderRadius: '999px',
          border: '2px solid ' + ink,
          background: over < 60000 ? 'var(--roze)' : '#fff',
          color: over < 60000 ? '#fff' : ink
        },

        // ---- aan het ophalen ----
        showKetLaden: this.state.ketMode === 'laden',
        ketLaden: 'Even ophalen…',
        ketLadenStyle: { fontSize: '13px', fontWeight: 600, color: 'var(--tekst-3)', textAlign: 'center' },

        /**
         * ---- de stippen ----
         *
         * Hoe ver het verhaal is, zonder er "stap 3 van 5" bij te schrijven. In het
         * midden, want het is de stand van het verhaal en niet een bijschrift bij
         * de opdracht ernaast.
         */
        ketStippen: !keten ? [] : Array.from({ length: ket.STAPPEN }, (_, i) => ({
          style: {
            width: '9px', height: '9px', borderRadius: '999px', flex: '0 0 auto',
            boxSizing: 'border-box', border: '2px solid ' + ink,
            background: i < keten.nr ? ink : i === keten.nr ? 'var(--roze)' : '#fff'
          }
        })),
        ketStipRijStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', minWidth: '0' },

        // ---- jouw beurt ----
        showKetDoe: this.state.ketMode === 'doe',
        ketKopStyle: { fontSize: '13px', fontWeight: 700, color: 'var(--tekst-2)', textAlign: 'center' },
        /**
         * ---- geen opdrachtregel ----
         *
         * Hier stond "Begin met een zin", "Teken dit" of "Wat is dit?". Die drie
         * zeiden alle drie wat je al ziet: een zin met een leeg vel en een doosje
         * kleuren eronder is tekenen, een tekening met een invulregel is raden. Een
         * regel die het beeld navertelt kost alleen ruimte.
         *
         * Wat blijft staan is de vraag boven de gezichtenkiezer. Die vertelt niets
         * na: zonder die regel is een raster van vierentwintig snuitjes een raadsel.
         * Zie sectie 13 van CLAUDE.md.
         */
        // Wat de vorige stap was volgt uit zijn nummer, niet uit een veld.
        ketVorigeZin: vorigeSoort === 'zin' ? (vorige.tekst || '') : '',
        showKetVorigeZin: vorigeSoort === 'zin',
        // De zin is het enige wat je hebt, dus die mag groot.
        ketVorigeZinStyle: {
          padding: '12px 14px', boxSizing: 'border-box', background: 'var(--geel-vlak)',
          border: '3px solid ' + ink, borderRadius: '12px',
          fontSize: '17px', fontWeight: 700, lineHeight: 1.3, textAlign: 'center'
        },
        ketVorigeSrc: vorigeSoort === 'krabbel' ? pixUrl(vorige.pixels) : '',
        showKetVorigeKrabbel: vorigeSoort === 'krabbel',
        ketVorigeKrabbelStyle: {
          width: '100%', maxWidth: '256px', aspectRatio: '1', height: 'auto',
          alignSelf: 'center', display: 'block',
          background: '#fff', border: '3px solid ' + ink, borderRadius: '12px',
          imageRendering: 'pixelated'
        },

        // ---- typen ----
        showKetTypen: soort === 'zin',
        ketZin: this.state.ketZin,
        ketTyp: this.ketTyp,
        ketZinToets: this.ketZinToets,
        ketZinPlaats: keten && keten.nr === 0 ? 'Een kat op een fiets' : '',
        ketZinMax: ket.ZIN_MAX,
        ketZinStyle: {
          width: '100%', boxSizing: 'border-box', padding: '11px 13px',
          border: '3px solid ' + ink, borderRadius: '12px', background: '#fff',
          fontSize: '16px', fontWeight: 600, fontFamily: 'inherit'
        },
        ketZinTel: (ket.ZIN_MAX - String(this.state.ketZin || '').length) + ' tekens over',
        showKetZinTel: String(this.state.ketZin || '').length > ket.ZIN_MAX - 12,
        ketZinTelStyle: { fontSize: '11px', fontWeight: 700, color: 'var(--tekst-3)', textAlign: 'right' },

        // ---- het doek, in het venster zelf ----
        showKetDoek: soort === 'krabbel',
        ketRef: this.ketRef,
        ketVak: ket.VAK,
        /**
         * Het doek: 384 bij 384 op een computer, 288 op een telefoon.
         *
         * Dat is 32 × 12 en 32 × 9, dus in beide gevallen hele pixels per vakje.
         * Bij een maat die niet deelbaar is wordt de ene kolom een pixel breder
         * dan de andere en zie je dat op een raster van tweeëndertig meteen.
         */
        // Plus de rand van 3 aan weerszijden: het doek is border-box, dus met 384 als
        // buitenmaat bleef er 378 voor de vakjes over, en dat zijn geen hele pixels.
        ketDoekVakStyle: {
          position: 'relative', width: '100%',
          maxWidth: (mob ? 288 : 384) + 6 + 'px',
          alignSelf: 'center', minWidth: '0'
        },
        ketDoekStyle: {
          width: '100%', aspectRatio: '1', height: 'auto', display: 'block',
          background: '#fff', border: '3px solid ' + ink, borderRadius: '12px',
          imageRendering: 'pixelated', touchAction: 'none', cursor: 'crosshair'
        },
        ketDown: this.ketDown,
        ketMove: this.ketMove,
        ketUp: this.ketUp,
        // Even breed als het doek erboven. Hij liep over de hele breedte van het
        // venster, en dan stak het gereedschap aan beide kanten onder het doek uit.
        ketVakStyleRij: {
          display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0,1fr))',
          gap: mob ? '6px' : '8px', minWidth: '0', flex: '0 0 auto',
          width: '100%', maxWidth: (mob ? 288 : 384) + 6 + 'px', alignSelf: 'center'
        },
        ketKleuren: PALETTE_HEX.map((hex, n) => ({
          onClick: () => this.ketKies(n),
          titel: this.state.ketSel ? 'Het kader vullen met kleur ' + (n + 1) : 'Kleur ' + (n + 1),
          gekozen: this.state.ketKleur === n,
          style: {
            width: '100%', aspectRatio: '1 / 1', borderRadius: '12px', background: hex,
            cursor: 'pointer', boxSizing: 'border-box', padding: '0', minWidth: '0',
            border: this.state.ketKleur === n ? '3px solid var(--roze)' : '3px solid ' + ink
          }
        })),
        ketPakPen: () => this.ketPakGereedschap('pen'),
        ketPakKader: () => this.ketPakGereedschap('sel'),
        ketPakGum: this.ketPakGum,
        ketWis: this.ketWis,
        ketPenStyle: {
          padding: '0', justifyContent: 'center', aspectRatio: '1 / 1', minWidth: '0',
          background: this.state.ketTool === 'pen' ? 'var(--paars)' : '#fff',
          color: this.state.ketTool === 'pen' ? '#fff' : ink
        },
        ketKaderStyle: {
          padding: '0', justifyContent: 'center', aspectRatio: '1 / 1', minWidth: '0',
          background: this.state.ketTool === 'sel' ? 'var(--paars)' : '#fff',
          color: this.state.ketTool === 'sel' ? '#fff' : ink
        },
        ketGumStyle: {
          width: '100%', aspectRatio: '1 / 1', display: 'flex', alignItems: 'center',
          justifyContent: 'center', borderRadius: '12px', cursor: 'pointer', padding: '0',
          boxSizing: 'border-box', minWidth: '0',
          border: this.state.ketKleur < 0 ? '3px solid var(--roze)' : '3px solid ' + ink,
          background: '#fff', color: ink
        },
        ketGumGekozen: this.state.ketKleur < 0,
        ketWisStyle: {
          width: '100%', aspectRatio: '1 / 1', boxSizing: 'border-box', padding: '0',
          justifyContent: 'center', borderRadius: '12px', minWidth: '0'
        },
        ketSelVak: (() => {
          const v = this.state.ketVoor;
          const r = v ? ket.rechthoek(v.a, v.b) : this.state.ketSel;
          return r ? { x: r.x, y: r.y, w: r.w, h: r.h } : null;
        })(),
        // Op de vakjes en niet op de rand eromheen, anders staat het kader scheef.
        ketSelSvgStyle: { position: 'absolute', inset: '3px', pointerEvents: 'none', zIndex: 2 },
        ketSelVakStyle: { animation: 'gpAvAnts .5s linear infinite' },

        /**
         * ---- het tempo, alleen als je zelf begint ----
         *
         * Rustig loopt door de dag; samen is negentig seconden tekenen en
         * vijfenveertig raden, en dan spreek je met elkaar af. Twee knoppen, en de
         * gekozen knop is paars. Ga je verder op iemands keten, dan is er niets te
         * kiezen: die loopt al op zijn eigen tempo.
         */
        showKetTempo: !!keten && keten.nr === 0,
        ketTempoRijStyle: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', minWidth: '0' },
        ketTempo: [
          {
            label: 'Rustig', regel: 'kwartier per stap',
            onClick: () => this.setState({ ketSnel: false }),
            style: tempoStyle(!this.state.ketSnel)
          },
          {
            label: 'Samen nu', regel: '90 s tekenen, 45 s raden',
            onClick: () => this.setState({ ketSnel: true }),
            style: tempoStyle(!!this.state.ketSnel)
          }
        ],
        ketTempoLabelStyle: { fontSize: '14px', fontWeight: 700, lineHeight: 1.2 },
        ketTempoRegelStyle: { fontSize: '11px', fontWeight: 600, lineHeight: 1.2, opacity: '.8' },

        // ---- doorgeven ----
        ketKlaarKnop: this.ketKlaar,
        ketKlaarLabel: 'Klaar, doorgeven',
        ketKlaarStyle: knopStyle('roze'),
        ketKlaarUit: !!this.state.ketBezig
          || (soort === 'zin' ? !ket.zinOk(this.state.ketZin) : !this.state.ketIets),

        // ---- naar wie gaat hij? ----
        showKetKiezen: this.state.ketMode === 'kiezen',
        ketKiesKop: 'Naar wie gaat hij?',
        /**
         * Een raster met gezichten, niet vijfentwintig rijen onder elkaar.
         *
         * Met een naam per rij werd dit venster bijna zeshonderd pixels hoog en
         * dekte het het halve scherm af. Iedereen hier kent elkaar aan zijn
         * krabbeltje, dus een raster van vijf breed past in vijf rijen en je vindt
         * iemand sneller. Welke naam eronder staat volgt wie je aanwijst; zo werkt
         * de kiezer bij een berichtje ook (dmCandidates in logic/render-vals.js).
         *
         * Iedereen behalve jezelf, en behalve degene van wie je hem net kreeg: die
         * zou zijn eigen zin terugkrijgen. Wie online is staat vooraan, want die
         * maakt het kwartier waarschijnlijk nog.
         */
        ketKandidaten: (() => {
          const ik = this.state.accountId || '';
          const vanWie = vorige ? String(vorige.authorId || '') : '';
          const nuMs = Date.now();
          const online = (a) => {
            const t = Date.parse(a.lastSeen || '');
            return !isNaN(t) && nuMs - t < 60000;
          };
          return (this.state.accounts || [])
            .filter((a) => a.id && a.id !== ik && a.id !== vanWie)
            // Tijdens de beta alleen de mensen die er zelf in kunnen. Zou je hem
            // naar iemand anders kunnen sturen, dan ligt die keten meteen stil bij
            // iemand die er niet in kan, en dat is één stap testen in plaats van
            // vijf. Zie isBetaTester in logic/arcade.js.
            .filter((a) => this.isBetaTester(a.id))
            .slice()
            .sort((a, b) => (online(b) ? 1 : 0) - (online(a) ? 1 : 0)
              || String(a.name || '').localeCompare(String(b.name || '')))
            .map((a) => ({
              name: a.name || 'Iemand',
              avatarUrl: avatarUrl(a.avatar),
              onPick: () => this.ketStuur({ id: a.id, name: a.name || '' }),
              onHover: () => this.setState({ ketWijst: a.name || 'Iemand' }),
              style: {
                position: 'relative', width: '100%', aspectRatio: '1 / 1', padding: '0',
                minWidth: '0', borderRadius: '12px', overflow: 'hidden',
                border: '3px solid ' + ink, background: '#fff'
              },
              avStyle: { width: '100%', height: '100%', display: 'block', imageRendering: 'pixelated' },
              // Het groene stipje in de hoek, net als in de mensenlijst.
              stipStyle: {
                position: 'absolute', top: '-4px', right: '-4px', width: '11px', height: '11px',
                borderRadius: '50%', border: '3px solid ' + ink,
                background: online(a) ? 'var(--groen)' : 'var(--uit)'
              }
            }));
        })(),
        ketKiesRasterStyle: {
          display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))',
          gap: '8px', minWidth: '0'
        },
        // Leeg tot je iemand aanwijst. Er stond "Wijs iemand aan om de naam te
        // zien", en dat is net zo'n regel die vertelt wat je al aan het doen bent.
        // Een vaste spatie houdt de hoogte, dus er springt niets zodra de naam
        // verschijnt.
        ketWijstNaam: this.state.ketWijst || '\u00a0',
        ketWijstStyle: {
          fontSize: '13px', fontWeight: 700, minHeight: '18px', lineHeight: '18px',
          textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          color: ink
        },
        ketWijstWeg: () => this.setState({ ketWijst: '' }),
        ketKiesLeeg: 'Nog geen andere spelers om hem naartoe te sturen.',
        showKetKiesLeeg: !(this.state.accounts || [])
          .filter((a) => a.id && a.id !== this.state.accountId && this.isBetaTester(a.id)).length,
        ketKiesTerug: () => this.setState({ ketMode: 'doe', ketWijst: '' }),
        ketKiesTerugLabel: 'Terug naar je stap',
        ketKiesTerugStyle: { fontSize: '12px', fontWeight: 700, color: 'var(--tekst-3)', background: 'none', border: 'none', padding: '4px' },

        // ---- teruglezen ----
        showKetLeesKnop: !!klaar.length && this.state.ketMode === 'doe' && !loopt,
        ketNaarLijst: this.ketNaarLijst,
        ketUitLijst: this.ketUitLijst,
        ketLeesKnopLabel: 'Teruglezen',
        ketLeesKnopStyle: { fontSize: '12px', fontWeight: 700, color: 'var(--tekst-3)', background: 'none', border: 'none', padding: '4px' },
        showKetLijst: this.state.ketMode === 'lijst',
        ketKlaarLijst: klaar.slice(0, 20).map((k) => ({
          naam: 'Keten van ' + this.ketNaam(k.stappen[0].authorId),
          wanneer: geleden(k.stappen[k.stappen.length - 1].created),
          onClick: () => this.ketLees(k.id),
          style: {
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
            width: '100%', padding: '9px 12px', boxSizing: 'border-box',
            border: '2px solid ' + ink, borderRadius: '12px', background: '#fff',
            fontSize: '13px', fontWeight: 700, textAlign: 'left'
          },
          wanneerStyle: { fontSize: '11px', fontWeight: 700, color: 'var(--tekst-3)', flex: '0 0 auto' }
        })),
        ketLijstStyle: { display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '0' },
        ketLijstLeeg: 'Nog geen klare ketens.',
        showKetLijstLeeg: !klaar.length,
        ketLijstLeegStyle: { fontSize: '13px', fontWeight: 600, color: 'var(--tekst-3)', lineHeight: 1.4, textAlign: 'center' },
        ketTerugStyle: knopStyle('wit'),

        /**
         * ---- teruglezen: één doorlopende strook ----
         *
         * De keten stond eerst onder elkaar en dan werd het venster negenhonderd
         * pixels hoog en scrollde je vijf keer naar beneden. Maar een keten loopt
         * niet naar beneden, hij loopt vooruit: van de eerste zin naar de laatste.
         * Dus staat hij nu naast elkaar en scroll je zijwaarts, als een filmstrook.
         *
         * De kaartjes zijn smaller dan het venster, met opzet. Er piept altijd een
         * stukje van de volgende om de hoek, en dat is het enige dat zegt dat er
         * meer is: de app heeft nergens zichtbare schuifbalken (zie styles.css).
         */
        showKetLees: this.state.ketMode === 'lees',
        ketLeesStrookStyle: {
          display: 'flex', flexDirection: 'row', alignItems: 'stretch', gap: '10px',
          overflowX: 'auto', overflowY: 'hidden', overscrollBehavior: 'contain',
          scrollSnapType: 'x proximity',
          paddingBottom: '2px', minWidth: '0', flex: '0 0 auto'
        },
        ketLeesStappen: (lezen ? lezen.stappen : []).map((s2, i) => {
          const eerste = i === 0;
          const laatste = i === lezen.stappen.length - 1;
          return {
            isZin: ket.soortVan(i) === 'zin',
            tekst: s2.tekst || '',
            src: ket.soortVan(i) === 'krabbel' ? pixUrl(s2.pixels) : '',
            naam: this.ketNaam(s2.authorId) + (eerste ? ' begon' : laatste ? ' maakte hem af' : ''),
            // Elk kaartje even breed en even hoog, zodat de strook een band is en
            // geen trapje. Tweehonderdvierentwintig is 32 × 7: hele pixels per
            // vakje, net als bij het doek waarop getekend is.
            style: {
              flex: '0 0 auto', width: '224px', scrollSnapAlign: 'center',
              display: 'flex', flexDirection: 'column', gap: '5px', minWidth: '0'
            },
            // De eerste en de laatste zin zijn de clou; die mogen geel oplichten.
            zinStyle: {
              height: '224px', boxSizing: 'border-box', padding: '12px 14px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: eerste || laatste ? 'var(--geel-vlak)' : '#fff',
              border: '3px solid ' + ink, borderRadius: '12px',
              fontSize: eerste || laatste ? '17px' : '15px',
              fontWeight: 700, lineHeight: 1.3, textAlign: 'center',
              overflow: 'hidden'
            },
            krabbelStyle: {
              width: '224px', height: '224px', display: 'block', boxSizing: 'border-box',
              background: '#fff', border: '3px solid ' + ink, borderRadius: '12px',
              imageRendering: 'pixelated'
            },
            naamStyle: {
              fontSize: '11px', fontWeight: 700, color: 'var(--tekst-3)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
            }
          };
        }),
        ketLeesKop: lezen ? 'Keten van ' + this.ketNaam(lezen.stappen[0].authorId) : 'Even ophalen…',
        /**
         * De clou onder de strook.
         *
         * In de strook staan de eerste en de laatste zin aan weerskanten, dus die
         * twee zie je nooit samen. Hier staan ze naast elkaar, en dat is precies
         * waar het spel om gaat.
         */
        ketLeesSlot: lezen && lezen.klaar
          ? '"' + (lezen.stappen[0].tekst || '') + '" werd "' + (lezen.stappen[lezen.stappen.length - 1].tekst || '') + '".'
          : '',
        showKetLeesSlot: !!(lezen && lezen.klaar),
        ketLeesSlotStyle: {
          padding: '9px 11px', boxSizing: 'border-box', background: 'var(--roze-balk)',
          border: '2px solid ' + ink, borderRadius: '12px',
          fontSize: '13px', fontWeight: 700, lineHeight: 1.4
        },
        ketAf: this.ketTerug,

        // ---- fouten en een lege server ----
        ketFout: this.state.ketFout || '',
        showKetFout: !!this.state.ketFout,
        ketFoutStyle: { fontSize: '12px', fontWeight: 700, color: 'var(--roze-ink)', lineHeight: 1.4 },
        ketBezig: !!this.state.ketBezig,
        /**
         * ---- als de server nog niet meewerkt ----
         *
         * Vier antwoorden en vier betekenissen. Het laatste is het lastigst te zien
         * en daarom staat het er apart: staan er wel stappen maar heeft geen enkele
         * een `tot`, dan kent de collectie de beurtvelden niet. Dan werkt het spel
         * nog wel, maar ligt elke keten voor iedereen open in plaats van bij één
         * iemand, en is er geen kwartier. Dat is precies het soort stille afwijking
         * waar je een uur naar zoekt, dus zegt de app het gewoon.
         */
        showKetLeeg: !!this.state.ketMissing
          || (!!(this.state.ketRijen || []).length && !lijst.length)
          || (!!lijst.length && !lijst.some((k) => k.tot)),
        ketLeeg: this.state.ketStatus === 403
          ? 'De Krabbellijn mag nog niet gelezen worden; de regels van de collectie staan dicht.'
          : this.state.ketMissing
            ? 'De Krabbellijn staat nog niet op de server. De collectie paal_ketting moet er nog komen.'
            : !lijst.length
              ? 'De collectie paal_ketting staat er wel, maar de velden keten en nr ontbreken.'
              : 'De collectie paal_ketting mist de velden naarId en tot. Zonder die twee is er geen beurt en geen kwartier: elke keten ligt dan voor iedereen open.',
        ketLeegStyle: { fontSize: '13px', fontWeight: 600, color: 'var(--tekst-3)', lineHeight: 1.4, textAlign: 'center' }
      };
    }
};
